import {
  Children,
  FC,
  useEffect,
  useMemo,
  isValidElement,
  useCallback,
  memo,
  lazy,
  Suspense
} from "react"
import { PageProps, HeadFC, graphql, withPrefix } from "gatsby"
import { useContentfulInspectorMode } from "@contentful/live-preview/react"
import { ContentfulLivePreview } from "@contentful/live-preview"

// redux
import { useDispatch, useSelector } from "react-redux"
import { setSearchQuery as setReduxSearchQuery } from "../../../../store/slices/search_query.slice"

// components
import Breadcrumbs from "../../../../components/Breadcrumbs/Breadcrumbs"
import { HeadUniversal } from "../../../../components/HeadUniversal/HeadUniversal"
import { Layout } from "../../../../components/Layout/Layout"
import { HrefLangTags } from "../../../../components/HrefLangTags/HrefLangTags"
import { CanonicalTag } from "../../../../components/CanonicalTag/CanonicalTag"
import { PersonalizationSection } from "../../../../components/PersonalizationSection/PersonalizationSection"
import { LogIn } from "../../../../components/LogIn/LogIn"

const RelatedArticles = lazy(
  () => import("../../../../components/RelatedArticles/RelatedArticles")
)
const VimeoVideo = lazy(() =>
  import("../../../../components/VimeoVideo/VimeoVideo").then((module) => ({
    default: module.VimeoVideo
  }))
)
const NumberedList = lazy(() =>
  import("../../../../components/NumberedList/NumberedList").then((module) => ({
    default: module.NumberedList
  }))
)

// utils
import {
  generateDescriptionMetaTag,
  generateRobotsMetaTag,
  getTitleText
} from "../../../../utils/seo/seo_utils"
import {
  enhancedContentfulRenderOptions,
  renderEnhancedRichText
} from "../../../../utils/contentful/enhanced-rich-text-renderer"
import { BLOCKS } from "@contentful/rich-text-types"
import { events } from "../../../../utils/heap/tracking_events"
import {
  assignLangCode,
  navigateWithPrefix
} from "../../../../utils/general_utils"

// types
import { RootState } from "../../../../store/store"
import { UserProfile } from "../../../../store/slices/user_profile.slice"
import { SingleHrefLangMetaDataSet } from "../../../../../gatsby-node"

// hooks
import { usePersonalizationComponent } from "../../../../hooks/usePersonalizationComponent"

// styles
import * as styles from "./Articles.module.scss"
import {
  Typography,
  ExpansionPanel,
  ExpansionPanelTrigger,
  ExpansionPanelContent
} from "@eventbrite/marmalade"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"

export type PageContext = {
  articleId: number
  id: string
  node_locale: string
  metaData: {
    hrefLangMetaData: { [langCode: string]: SingleHrefLangMetaDataSet }
    replacementHelpArticleBodyMigrationRawData?: string
    replacementSummaryRawData?: string
  }
}

const AccordionItem = memo(
  ({
    header,
    body,
    references,
    expandByDefault,
    contentfulId,
    onOpenChange
  }: {
    header: string
    body: any
    references: any[]
    expandByDefault?: boolean
    contentfulId?: string
    onOpenChange?: (open: boolean) => void
  }) => {
    return (
      <div style={{ display: "flex", width: "100%", marginBottom: "16px" }}>
        <ExpansionPanel
          variant="container"
          defaultOpen={expandByDefault}
          onOpenChange={onOpenChange}
        >
          <ExpansionPanelTrigger className={styles.expansionPanelTrigger}>
            {header}
          </ExpansionPanelTrigger>
          <ExpansionPanelContent>
            <div className={styles.accordionContent}>
              {body?.raw &&
                renderEnhancedRichText(body.raw, {
                  references: references
                })}
            </div>
          </ExpansionPanelContent>
        </ExpansionPanel>
      </div>
    )
  }
)

const TableOfContents = memo(
  ({
    tableOfContents,
    tableOfContentsClick,
    headerTableOfContents
  }: {
    tableOfContents: string[]
    tableOfContentsClick: (textContent: string) => void
    headerTableOfContents: any
  }) => {
    if (tableOfContents.length === 0) return null

    return (
      <>
        <span className={styles.tableOfContentsHeader}>
          {renderEnhancedRichText(headerTableOfContents?.microcopyValue?.raw)}
        </span>
        <ul className={styles.tableOfContents}>
          {tableOfContents.map((heading, index) => (
            <li
              key={`${heading}__${index}`}
              onClick={() => tableOfContentsClick(heading)}
            >
              <Typography variant="body-md" as="p" color="none">
                {heading}
              </Typography>
            </li>
          ))}
        </ul>
      </>
    )
  }
)

const ArticleSections = memo(
  ({
    sections,
    articleBodySalesforceMigrationRawString,
    references,
    customRenderers,
    articleEntryId
  }: {
    sections?: readonly any[] | null
    articleBodySalesforceMigrationRawString: string
    references: readonly any[]
    customRenderers?: any
    articleEntryId?: string
  }) => {
    const inspectorProps = useContentfulInspectorMode({
      entryId: articleEntryId
    })

    if (sections) {
      return (
        <>
          {sections.map((section, index) => (
            <div key={`section-${index}`}>
              <h2
                {...ContentfulLivePreview.getProps({
                  entryId: section?.contentful_id,
                  fieldId: "sectionHeader"
                })}
              >
                {section.sectionHeader}
              </h2>
              {section?.articleSectionBody?.raw &&
                section.articleSectionBody?.raw.length > 0 &&
                renderEnhancedRichText(section?.articleSectionBody?.raw, {
                  references:
                    section?.articleSectionBody?.references?.length > 0
                      ? section?.articleSectionBody?.references
                      : [],
                  renderNode: customRenderers
                })}
            </div>
          ))}
        </>
      )
    }

    return (
      <>
        {renderEnhancedRichText(articleBodySalesforceMigrationRawString, {
          references: references,
          renderNode: customRenderers
        })}
      </>
    )
  }
)

export const HelpArticle: FC<PageProps<Queries.HelpArticleQuery, PageContext>> =
  memo((pageProps) => {
    const data = pageProps?.data
    const inspectorProps = useContentfulInspectorMode({
      entryId: data?.contentfulHelpArticle?.contentful_id
    })

    const prefixedBasePath = withPrefix(
      `/${pageProps?.pageContext["node_locale"]?.toLowerCase()}`
    )

    if (!data?.contentfulHelpArticle) {
      return null
    }

    const userProfile = useSelector(
      (state: RootState) => state.userProfile
    ) as UserProfile | null
    const currentLangCode = useSelector(
      (state: RootState) => state.metaData.langCode
    )
    const dispatch = useDispatch()

    const audience =
      data?.contentfulHelpArticle?.articleAudience?.english || "Attendee"

    const { activePersonalizationComponent } = usePersonalizationComponent(
      audience,
      "article"
    )

    const setSearchQuery = useCallback(
      (q: string) => {
        dispatch(setReduxSearchQuery(q))
      },
      [dispatch]
    )

    const articleBodySalesforceMigrationRawString = useMemo<string>(
      () =>
        pageProps?.pageContext?.metaData
          ?.replacementHelpArticleBodyMigrationRawData ||
        data?.contentfulHelpArticle?.articleBodySalesforceMigration?.raw ||
        "",
      [
        pageProps?.pageContext?.metaData
          ?.replacementHelpArticleBodyMigrationRawData,
        data?.contentfulHelpArticle?.articleBodySalesforceMigration?.raw
      ]
    )

    const summaryRawString = useMemo<string>(
      () =>
        pageProps?.pageContext?.metaData?.replacementSummaryRawData ||
        data?.contentfulHelpArticle?.summary?.raw ||
        "",
      [
        pageProps?.pageContext?.metaData?.replacementSummaryRawData,
        data?.contentfulHelpArticle?.summary?.raw
      ]
    )

    const articles = useMemo(() => {
      if (!data?.contentfulHelpArticle?.relatedArticles) return []

      return data.contentfulHelpArticle.relatedArticles.map((art) => ({
        articleId: art?.articleId ?? null,
        helpArticleTitle: art?.helpArticleTitle ?? null,
        slug: art?.slug ?? null
      }))
    }, [data?.contentfulHelpArticle?.relatedArticles])

    const tableOfContents = useMemo(() => {
      const headers: string[] = []

      if (data?.contentfulHelpArticle?.sections) {
        data.contentfulHelpArticle.sections.forEach((section) => {
          if (section?.sectionHeader) {
            headers.push(section.sectionHeader)
          }
        })
      } else {
        Children.forEach(
          renderEnhancedRichText(articleBodySalesforceMigrationRawString, {
            references:
              (data?.contentfulHelpArticle?.articleBodySalesforceMigration
                ?.references?.length ?? 0) > 0
                ? data?.contentfulHelpArticle?.articleBodySalesforceMigration
                    ?.references
                : []
          }),
          (child) => {
            if (isValidElement(child) && child.type === "h2") {
              if (typeof child.props.children[0] !== "string") {
                headers.push(child.props.children[0].props.children)
              } else {
                headers.push([...child.props.children].join(""))
              }
            }
          }
        )
      }

      return headers
    }, [
      data?.contentfulHelpArticle?.sections,
      articleBodySalesforceMigrationRawString,
      data?.contentfulHelpArticle?.articleBodySalesforceMigration?.references
    ])

    const tableOfContentsClick = useCallback((textContent: string) => {
      const headers = document.querySelectorAll("h2")
      for (const header of headers) {
        if (header.textContent === textContent) {
          header.scrollIntoView({ behavior: "smooth" })
          break
        }
      }
    }, [])

    useEffect(() => {
      const idleCallback = () => {
        const olElements = document.querySelectorAll("ol")
        const olTypes = {
          1: "A",
          2: "i",
          3: "a"
        } as const

        olElements.forEach((ol) => {
          const isNested = ol.closest("ol") !== null
          if (isNested) {
            let nestingLevel = 0
            let parentElement = ol.parentElement

            while (parentElement && nestingLevel < 15) {
              if (parentElement.tagName === "OL") {
                nestingLevel++
              }
              parentElement = parentElement.parentElement
            }
            if (nestingLevel > 0 && nestingLevel <= 3) {
              ol.setAttribute(
                "type",
                olTypes[nestingLevel as keyof typeof olTypes]
              )
            }
          }
        })
      }

      if ("requestIdleCallback" in window) {
        requestIdleCallback(idleCallback)
      } else {
        setTimeout(idleCallback, 100)
      }
    }, [])
    useEffect(() => {
      if (typeof window === "undefined") return

      const contentfulArticleSlug = data?.contentfulHelpArticle?.slug
      const articleId = pageProps?.params?.articleId
      const currentPath = window.location.pathname

      if (!contentfulArticleSlug || !articleId) return

      const shouldRedirect =
        (currentPath.includes("[slug]") &&
          !contentfulArticleSlug.includes("[slug]")) ||
        (currentPath.includes("%5Bslug%5D") &&
          !contentfulArticleSlug.includes("%5Bslug%5D"))

      if (shouldRedirect) {
        navigateWithPrefix(`/articles/${articleId}/${contentfulArticleSlug}/`, {
          replace: true
        })
      }
    }, [data?.contentfulHelpArticle?.slug, pageProps?.params?.articleId])

    // Defer non-critical tracking
    useEffect(() => {
      const trackingCallback = () => {
        if (userProfile) {
          events?.userProfileData("User Profile", userProfile)
        }

        // save or update langCode
        assignLangCode(currentLangCode, dispatch)

        // set search query to empty string
        setSearchQuery("")

        // fire heap event
        events?.articlePageData("Article Visited", {
          requiresAssistance:
            data?.contentfulHelpArticle?.requiresAssistance ?? false,
          helpArticleAssistanceCTAs:
            data?.contentfulHelpArticle?.helpArticleAssistanceCTAs?.join(
              "--"
            ) ?? null,
          helpArticleCategory:
            data?.contentfulHelpArticle?.helpArticleCategory?.categoryTitle ??
            "",
          helpArticleSubcategory:
            data?.contentfulHelpArticle?.helpArticleSubcategory
              ?.subcategoryHeader ?? "",
          articleId: data?.contentfulHelpArticle?.articleId ?? 0
        })
      }

      if ("requestIdleCallback" in window) {
        requestIdleCallback(trackingCallback)
      } else {
        setTimeout(trackingCallback, 0)
      }
    }, [userProfile, currentLangCode, dispatch, setSearchQuery, data])

    const references = useMemo(
      () =>
        (data?.contentfulHelpArticle?.articleBodySalesforceMigration?.references
          ?.length ?? 0) > 0
          ? data?.contentfulHelpArticle?.articleBodySalesforceMigration
              ?.references
          : [],
      [data?.contentfulHelpArticle?.articleBodySalesforceMigration?.references]
    )

    // Create a custom renderer that handles accordion panels and numbered lists
    // while preserving the default behavior for everything else
    const customRenderers = useMemo(() => {
      // Get accordion panels from both articleBodySalesforceMigration and sections
      const articleBodyAccordionPanels =
        data?.contentfulHelpArticle?.articleBodySalesforceMigration?.references?.filter(
          (ref: any) => ref.__typename === "ContentfulAccordionPanel"
        ) || []

      const sectionsAccordionPanels =
        data?.contentfulHelpArticle?.sections?.flatMap(
          (section: any) =>
            section?.articleSectionBody?.references?.filter(
              (ref: any) => ref.__typename === "ContentfulAccordionPanel"
            ) || []
        ) || []

      // Get numbered lists from both articleBodySalesforceMigration and sections
      const articleBodyNumberedLists =
        data?.contentfulHelpArticle?.articleBodySalesforceMigration?.references?.filter(
          (ref: any) => ref.__typename === "ContentfulNumberedList"
        ) || []

      const sectionsNumberedLists =
        data?.contentfulHelpArticle?.sections?.flatMap(
          (section: any) =>
            section?.articleSectionBody?.references?.filter(
              (ref: any) => ref.__typename === "ContentfulNumberedList"
            ) || []
        ) || []

      // Combine all accordion panels and numbered lists
      const allAccordionPanels = [
        ...articleBodyAccordionPanels,
        ...sectionsAccordionPanels
      ]

      const allNumberedLists = [
        ...articleBodyNumberedLists,
        ...sectionsNumberedLists
      ]

      // Return only the custom renderers - everything else will be inherited
      return {
        [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
          const entryId = node.data.target.sys.id

          // Check for accordion panel
          const accordionPanel = allAccordionPanels.find(
            (panel: any) => panel.contentful_id === entryId
          )

          if (accordionPanel) {
            return (
              <AccordionItem
                key={entryId}
                header={accordionPanel.accordionPanelHeader}
                body={accordionPanel.accordionPanelBody}
                references={accordionPanel.accordionPanelBody?.references || []}
                expandByDefault={accordionPanel.accordionPanelExpandBoolean}
                contentfulId={accordionPanel.contentful_id}
                onOpenChange={(open) =>
                  events?.articleAccordionInteraction(
                    accordionPanel.accordionPanelHeader ?? "",
                    accordionPanel.contentful_id ?? "",
                    open ? "open" : "close",
                    "AccordionItem",
                    data?.contentfulHelpArticle?.articleId ?? undefined
                  )
                }
              />
            )
          }

          // Check for numbered list
          const numberedList = allNumberedLists.find(
            (list: any) => list.contentful_id === entryId
          )

          if (numberedList) {
            return (
              <Suspense fallback={<div>Loading...</div>}>
                <NumberedList
                  key={entryId}
                  data={numberedList}
                  articleId={
                    data?.contentfulHelpArticle?.articleId ?? undefined
                  }
                />
              </Suspense>
            )
          }

          const defaultEmbeddedEntryRenderer =
            enhancedContentfulRenderOptions?.renderNode?.[BLOCKS.EMBEDDED_ENTRY]
          return defaultEmbeddedEntryRenderer
            ? defaultEmbeddedEntryRenderer(node, null)
            : null
        }
      }
    }, [
      data?.contentfulHelpArticle?.articleBodySalesforceMigration?.references,
      data?.contentfulHelpArticle?.sections
    ])

    return (
      <Layout
        gatsbyPageProps={pageProps}
        searchBar={{ isSearchBarVisible: true, isResultsBoxShown: true }}
        microCopy={{
          buttonBrowseHelpCenter: data?.buttonBrowseHelpCenter,
          buttonContactUs: data?.buttonContactUs,
          messageContactUs: data?.messageContactUs,
          upperNavMicroCopySet: data?.upperNavMicroCopySet,
          headerSearchResults: data?.headerSearchResults,
          messageSearchBar: data?.messageSearchBar,
          chatbotUITranslations: data?.chatbotUITranslations
        }}
        footerCopy={data?.contentfulFooter?.footerReference ?? null}
        footerNavCopy={
          data?.contentfulFooterNavigation?.FooterNavigationChildItems ?? null
        }
      >
        <div className={styles?.articlePage}>
          <Breadcrumbs
            topic={data?.contentfulCategoryMenu?.categoryTitle ?? undefined}
            slug={data?.contentfulCategoryMenu?.categoryMenuSlug ?? undefined}
            contentfulMicrocopy={data?.navigationHomepage}
            prefixedBasePath={prefixedBasePath}
            nodeLocale={pageProps?.pageContext?.node_locale}
          />

          {data?.contentfulHelpArticle?.articleAudience?.articleEyebrowText && (
            <div>
              <Typography variant="body-md-bold">
                {
                  data?.contentfulHelpArticle?.articleAudience
                    ?.articleEyebrowText
                }
              </Typography>
            </div>
          )}

          <div className={styles?.articleHeaderSection}>
            <h1
              className={styles?.articleHeader}
              {...inspectorProps({ fieldId: "helpArticleTitle" })}
            >
              {data?.contentfulHelpArticle?.helpArticleTitle}
            </h1>
          </div>

          <div className={styles?.contentWrap}>
            <div
              className={styles.innerContentWrapper}
              {...inspectorProps({ fieldId: "articleBodySalesforceMigration" })}
            >
              {data?.contentfulHelpArticle?.summary && (
                <div
                  className={styles?.summarySection}
                  {...inspectorProps({ fieldId: "summary" })}
                >
                  {renderEnhancedRichText(summaryRawString || undefined, {
                    references:
                      data?.contentfulHelpArticle?.summary?.references || []
                  })}
                </div>
              )}

              {data?.contentfulHelpArticle?.articleVideo && (
                <Suspense
                  fallback={
                    <div style={{ height: "300px", background: "#f5f5f5" }} />
                  }
                >
                  <VimeoVideo
                    src={
                      data?.contentfulHelpArticle?.articleVideo?.videoUrl ?? ""
                    }
                    thumbnailUrl={
                      data?.contentfulHelpArticle?.articleVideo
                        ?.videoThumbnailUrl ?? undefined
                    }
                    caption={
                      data?.contentfulHelpArticle?.articleVideo?.videoCaption
                        ?.videoCaption ?? undefined
                    }
                  />
                </Suspense>
              )}

              <TableOfContents
                tableOfContents={tableOfContents}
                tableOfContentsClick={tableOfContentsClick}
                headerTableOfContents={data?.headerTableOfContents}
              />

              <ArticleSections
                sections={data.contentfulHelpArticle.sections}
                articleBodySalesforceMigrationRawString={
                  articleBodySalesforceMigrationRawString
                }
                references={references ?? []}
                customRenderers={customRenderers}
                articleEntryId={data.contentfulHelpArticle.contentful_id}
              />

              <Suspense fallback={<div>Loading related articles...</div>}>
                <RelatedArticles
                  articles={articles}
                  relatedArticlesHeader={
                    data?.headerRelatedArticles?.microcopyValue
                  }
                />
              </Suspense>
            </div>

            <div className={styles.loginWrapper}>
              <Suspense fallback={<div>Loading...</div>}>
                {activePersonalizationComponent === "login" ? (
                  <LogIn
                    loginButtonMicrocopy={data?.loginButtonMicrocopy}
                    loginContentMicrocopy={data?.loginContentMicrocopy}
                    loginHeaderMicrocopy={data?.loginHeaderMicrocopy}
                    cantLogInMicrocopy={data?.cantLogInMicrocopy}
                    cantLogInArticle={data?.cantLogInArticle}
                    mobileSizeOnly={true}
                  />
                ) : (
                  <PersonalizationSection
                    audienceOverride={audience}
                    columnLayout={true}
                    mobileSizeOnly={true}
                    mode="article"
                    messageNoUpcomingOrders={data?.messageNoUpcomingOrders}
                    noTicketsFoundActionComplex={
                      data?.noTicketsFoundActionComplex
                    }
                    ticketsFoundActionComplex={data?.ticketsFoundActionComplex}
                    searchingForTickets={data?.searchingForTickets}
                    noEventsFoundActionComplex={
                      data?.noEventsFoundActionComplex
                    }
                    eventsFoundActionComplex={data?.eventsFoundActionComplex}
                    searchingForEvents={data?.searchingForEvents}
                    messageNoUpcomingEvents={data?.messageNoUpcomingEvents}
                    loginButtonMicrocopy={data?.loginButtonMicrocopy}
                    loginContentMicrocopy={data?.loginContentMicrocopy}
                    loginHeaderMicrocopy={data?.loginHeaderMicrocopy}
                  />
                )}
              </Suspense>
            </div>
          </div>
        </div>
      </Layout>
    )
  })

HelpArticle.displayName = "HelpArticle"

export const query = graphql`
  query HelpArticle($articleId: Int, $node_locale: String) {
    # PERSONALIZATION
    # FIND MY TICKETS
    messageNoUpcomingOrders: contentfulMicrocopy(
      microcopyKey: { eq: "messageNoUpcomingOrders" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyValue {
        raw
      }
    }

    searchingForTickets: contentfulEventActionComplex(
      contentful_id: { eq: "HGMxLdiIS4NgziflHZXtU" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexLoadingMessage {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    ticketsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "7qfRoL77Rw1J9AFa7IcKnv" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    noTicketsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "4VjzGzF1EYJKdWthSHYRrH" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }
    # END FIND MY TICKETS

    # FIND MY EVENTS
    messageNoUpcomingEvents: contentfulMicrocopy(
      microcopyKey: { eq: "messageNoUpcomingEvents" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyValue {
        raw
      }
    }

    searchingForEvents: contentfulEventActionComplex(
      contentful_id: { eq: "7dL10FjtyQxQglQ0RH1tos" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexLoadingMessage {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    noEventsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "6DJb2f0Kj2xvTlsFsTx0Yd" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    eventsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "63OCAocrPDWrso7ye5VBdp" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }
    # END FIND MY EVENTS
    # END PERSONALIZATION

    # LOGIN
    loginContentMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "messageLoginResources" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      node_locale
      microcopyInternalTitle
      microcopyKey
      microcopyValue {
        raw
      }
    }

    loginHeaderMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "headerLoginResources" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    loginButtonMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "navigationLogIn" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    cantLogInMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "navigationCantLogIn" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    cantLogInArticle: contentfulHelpArticle(
      articleId: { eq: 221530 }
      node_locale: { eq: $node_locale }
    ) {
      articleId
      slug
    }
    # END LOGIN

    chatbotUITranslations: allContentfulMicrocopySet(
      filter: {
        microcopySetInternalName: { eq: "Chatbot UI" }
        node_locale: { eq: $node_locale }
      }
    ) {
      edges {
        node {
          node_locale
          microcopySetEntries {
            ... on ContentfulMicrocopy {
              contentful_id
              microcopyKey
              microcopyValue {
                raw
              }
            }
          }
        }
      }
    }

    homepageSeoData: contentfulSeoMetaTags(
      seoMetaTagInternalTitle: { eq: "Homepage" }
      node_locale: { eq: $node_locale }
    ) {
      seoMetaTagTitle
    }

    messageSearchBar: contentfulMicrocopy(
      microcopyKey: { eq: "messageSearchBar" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerSearchResults: contentfulMicrocopy(
      microcopyKey: { eq: "headerSearchResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    buttonContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "buttonContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    buttonBrowseHelpCenter: contentfulMicrocopy(
      microcopyKey: { eq: "buttonBrowseHelpCenter" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerTableOfContents: contentfulMicrocopy(
      microcopyInternalTitle: { eq: "Header: Article Table of Contents" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerRelatedArticles: contentfulMicrocopy(
      microcopyInternalTitle: { eq: "Header: Related articles" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    messageContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "messageStillHaveQuestions" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    upperNavMicroCopySet: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Help Center nav" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    navigationHomepage: contentfulMicrocopy(
      microcopyKey: { eq: "navigationHomepage" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    contentfulFooter(node_locale: { eq: $node_locale }) {
      id
      footerInternalName
      footerReference {
        displayText
        FooterNavigationChildItems {
          footerNavigationInternalTitle
          displayText
          footerNavigationUrl
        }
      }
    }
    contentfulFooterNavigation(
      footerNavigationInternalTitle: { eq: "Category: Bottom row navigation" }
      node_locale: { eq: $node_locale }
    ) {
      footerNavigationInternalTitle
      footerNavigationUrl
      FooterNavigationChildItems {
        footerNavigationInternalTitle
        displayText
        footerNavigationUrl
      }
    }

    contentfulHelpArticle(
      articleId: { eq: $articleId }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      articleId
      node_locale
      helpArticleTitle
      slug
      updatedAt
      summary {
        raw
        references {
          __typename
          ... on ContentfulEntry {
            ... on ContentfulAlert {
              __typename
              contentful_id
              alertShowIcon
              alertHeader
              alertCta {
                microcopyValue {
                  raw
                }
              }
              alertDescription {
                raw
              }
              alertDismissBoolean
              alertTypeReference {
                alertTypeName
              }
            }
          }
        }
      }
      sections {
        contentful_id
        sectionHeader
        articleSectionBody {
          raw
          references {
            __typename
            ... on ContentfulEntry {
              ... on ContentfulAlert {
                __typename
                contentful_id
                alertShowIcon
                alertHeader
                alertCta {
                  microcopyValue {
                    raw
                  }
                }
                alertDescription {
                  raw
                }
                alertDismissBoolean
                alertTypeReference {
                  alertTypeName
                }
              }
              ... on ContentfulImage {
                contentful_id
                image {
                  title
                  description
                  width
                  height
                  __typename
                  mimeType
                  url
                  gatsbyImageData(width: 1000)
                }
              }
              ... on ContentfulVideo {
                contentful_id
                videoTitle
                videoUrl
                videoThumbnailUrl
                videoCaption {
                  videoCaption
                }
                videoThumbnailAltText {
                  videoThumbnailAltText
                }
              }
              ... on ContentfulAccordionPanel {
                __typename
                contentful_id
                accordionPanelHeader
                accordionPanelExpandBoolean
                accordionPanelBody {
                  raw
                }
              }
              ... on ContentfulNumberedList {
                __typename
                contentful_id
                numberedListInternalTitle
                numberedListVariations {
                  __typename
                  contentful_id
                  numberedListVariationTitle
                  numberedListVariationSteps {
                    raw
                  }
                }
              }
            }
          }
        }
      }
      articleBodySalesforceMigration {
        raw
        __typename
        references {
          __typename
          ... on ContentfulEntry {
            ... on ContentfulAlert {
              __typename
              contentful_id
              alertShowIcon
              alertHeader
              alertCta {
                microcopyValue {
                  raw
                }
              }
              alertDescription {
                raw
              }
              alertDismissBoolean
              alertTypeReference {
                alertTypeName
              }
            }

            ... on ContentfulImage {
              contentful_id
              image {
                title
                description
                width
                height
                __typename
                mimeType
                url
                gatsbyImageData(width: 1000)
              }
            }
            ... on ContentfulVideo {
              contentful_id
              videoTitle
              videoUrl
              videoThumbnailUrl
              videoCaption {
                videoCaption
              }
              videoThumbnailAltText {
                videoThumbnailAltText
              }
            }
            ... on ContentfulAccordionPanel {
              __typename
              contentful_id
              accordionPanelHeader
              accordionPanelExpandBoolean
              accordionPanelBody {
                raw
              }
            }
            ... on ContentfulNumberedList {
              __typename
              contentful_id
              numberedListInternalTitle
              numberedListVariations {
                __typename
                contentful_id
                numberedListVariationTitle
                numberedListVariationSteps {
                  raw
                }
              }
            }
          }
        }
      }
      relatedArticles {
        articleId
        helpArticleTitle
        slug
      }
      requiresAssistance
      helpArticleAssistanceCTAs
      helpArticleCategory {
        categoryTitle
      }
      helpArticleSubcategory {
        subcategoryHeader
      }
      articleAudience {
        english
        articleEyebrowText
      }
      articleVideo {
        contentful_id
        videoTitle
        videoUrl
        videoThumbnailUrl
        videoCaption {
          videoCaption
        }
        videoThumbnailAltText {
          videoThumbnailAltText
        }
      }
    }
    contentfulCategoryMenu(
      node_locale: { eq: $node_locale }
      subcategories: {
        elemMatch: {
          articleList: {
            elemMatch: {
              articleId: { eq: $articleId }
              node_locale: { eq: $node_locale }
            }
          }
        }
      }
    ) {
      categoryTitle
      categoryMenuSlug
    }
  }
`

const filterDescription = (desc: any): string => {
  if (typeof desc === "string") {
    if (desc?.length > 0) {
      if (!desc?.toLowerCase()?.includes("[object")) {
        return desc?.length >= 150 ? `${desc?.slice(0, 150)}...` : desc
      }
    }
  }

  return ""
}

export const Head: HeadFC<Queries.HelpArticleQuery, PageContext> = (
  headArgs
) => {
  const hrefLangMetaData = useMemo<{
    [langCode: string]: SingleHrefLangMetaDataSet
  }>(() => headArgs?.pageContext?.metaData?.hrefLangMetaData, [headArgs])

  const canonicalUrl = useMemo<string>(() => {
    const nodeLocale = headArgs?.pageContext?.node_locale

    if (nodeLocale) {
      return hrefLangMetaData
        ? nodeLocale
          ? hrefLangMetaData[nodeLocale]?.href
          : ""
        : ""
    }

    if (headArgs?.location?.pathname?.length > 0) {
      return headArgs?.location?.pathname
    }

    return ""
  }, [headArgs])

  const title = headArgs?.data?.contentfulHelpArticle?.helpArticleTitle
  const description = documentToPlainTextString(
    headArgs?.data?.contentfulHelpArticle?.summary?.raw
      ? JSON.parse(headArgs?.data?.contentfulHelpArticle?.summary?.raw)
      : ""
  )

  const finalDescription = filterDescription(description)

  const siteTitle = headArgs?.data?.homepageSeoData?.seoMetaTagTitle

  // shouldNoIndex was driven off helpArticleSeoMetadata.seoMetaTagRobotsIndex
  // (always undefined at runtime per above), so it was always false anyway.
  const shouldNoIndex = false

  return (
    <>
      <HeadUniversal
        {...headArgs}
        metaTitle={`${title}${siteTitle ? ` | ${siteTitle}` : ""}`}
        metaDescription={finalDescription}
      />
      {headArgs?.data?.contentfulHelpArticle?.helpArticleTitle && (
        <>
          {getTitleText(`${title}${siteTitle ? ` | ${siteTitle}` : ""}`)}
          {generateDescriptionMetaTag(finalDescription)}
          {/* Articles must stay indexable. (true, true) renders no robots tag;
              (false, false) emitted `noindex, nofollow` and deindexed every article. */}
          {generateRobotsMetaTag(true, true)}
        </>
      )}
      <HrefLangTags metaData={hrefLangMetaData} shouldNoIndex={shouldNoIndex} />
      <CanonicalTag
        href={canonicalUrl}
        nodeLocale={headArgs?.pageContext?.node_locale}
        shouldNoIndex={shouldNoIndex}
      />
    </>
  )
}

export default HelpArticle
